import { Dispatch, SetStateAction } from "react";
import * as Yup from "yup";

import { DECIMAL_PLACES_DEFAULT } from "@/constants";
import { getFileNameAndExtension } from "@/helpers";

import { SupplyOrderProps, SupplyQuotationProps } from "@/types";
import { SupplyOrderValues } from "../../types";

export const schemaPO = (t: (key: string) => string) => ({
  supplierUuid: Yup.string().required(t("requiredMessage")),
  items: Yup.array()
    .of(Yup.mixed())
    .required(t("requiredMessageItemsForUpdate"))
    .min(1, t("requiredMessageItemsForUpdate")),
});

const handleDownloadFile = async (url: string) => {
  if (!url) return undefined;

  const file = getFileNameAndExtension(url || "");

  const response = await fetch(url);
  const blob = await response.blob();

  return new File([blob], file.fileName, { type: file.extension });
};

export const populateUpdateSupplyOrder = async (
  setValues: Dispatch<SetStateAction<SupplyOrderValues>>,
  data?: SupplyOrderProps
) => {
  if (data) {
    const values: SupplyOrderValues = {
      supplier: data.supplier,
      currency: data.payments?.[0]?.currency,
      expectedPaymentDate: data.payments?.[0]?.expectedPaymentDate,
      paymentPriority: data.payments?.[0]?.paymentPriority,
      observation: data.observation,
      includeIva: data?.includeIva,
      purchaseOrderCode: data.supplyQuotation?.purchaseOrderCode,
      status: data?.status,
      shipmentStatus: data?.shipmentStatus,
      cancellationReason: data?.cancellationReason,
      cancellationDescription: data?.cancellationDescription,
    };

    values.supplyOrderItems = data.items ? [...data.items] : [];

    try {
      if (data.invoice?.pdfFile) {
        const url = data.invoice?.pdfFile;

        const file = await handleDownloadFile(url);
        if (file) {
          values.invoicePDFFile = file;
        }
      }

      if (data.invoice?.xmlFile) {
        const url = data.invoice?.xmlFile;

        const file = await handleDownloadFile(url);
        if (file) {
          values.invoiceXMLFile = file;
        }
      }
    } catch (error) {}

    setValues((prev) => ({
      ...prev,
      ...values,
    }));
  }
};

export const generateSupplyOrderPayload = (
  values: SupplyOrderValues,
  paymentUuid?: string,
  supplyQuotationUuid?: string
): Partial<SupplyOrderProps> => {
  const result: Partial<SupplyOrderProps> = {
    items: [],
    status: values?.status,
    shipmentStatus: values?.shipmentStatus,
    action: values?.action,
    cancellationReason: values?.cancellationReason,
    cancellationDescription: values?.cancellationDescription,
    observation: values?.observation,
    supplierUuid: values?.supplier?.uuid ?? "",
    supplier: undefined,
    invoice: undefined,
    ...(paymentUuid
      ? {
          payments: [
            {
              paymentPriority: values?.paymentPriority,
              expectedPaymentDate: values?.expectedPaymentDate,
              uuid: paymentUuid,
            },
          ],
        }
      : {}),
    ...(supplyQuotationUuid
      ? {
          supplyQuotation: {
            purchaseOrderCode: values?.purchaseOrderCode,
            uuid: supplyQuotationUuid,
          } as SupplyQuotationProps,
        }
      : {}),
  };

  values?.supplyOrderItems?.forEach((item) => {
    if (!item?.quantity) return;

    result.items?.push({
      cost: String(Number(item?.cost).toFixed(DECIMAL_PLACES_DEFAULT)) || "0",
      quantity: item?.quantity ?? 0,
      supplierWarehouseUuid: values?.supplierWarehouseUuid || undefined,
      deliveryOption: item?.deliveryOption ?? undefined,
      catalogProductUuid:
        item?.catalogProduct?.uuid ?? item?.catalogProductUuid ?? undefined,
      uuid: item?.uuid,
      category: item?.category ?? undefined,
    });
  });

  return result;
};
